import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import './WidgetModal.css';

const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'api-gateway', label: 'API Gateway' },
  { value: 'user-service', label: 'User Service' },
  { value: 'order-service', label: 'Order Service' },
  { value: 'payment-service', label: 'Payment Service' },
  { value: 'notification-service', label: 'Notification Service' },
  { value: 'auth-service', label: 'Auth Service' }
];

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'orange', label: 'Orange' },
  { value: 'red', label: 'Red' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'pink', label: 'Pink' },
  { value: 'teal', label: 'Teal' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'gray', label: 'Gray' }
];

const emptyLogsDataset = () => ({
  id: `ds-${Date.now()}`,
  label: 'Series',
  color: 'blue',
  service: '',
  logLevel: '',
  keyword: '',
  logql: '',
  metric: 'cpu'
});

/**
 * Nested modal to edit one dataset (sub-series) inside a widget.
 */
const DatasetEditorModal = ({ isOpen, onClose, onSave, dataset, dataSource, darkMode = false }) => {
  const [local, setLocal] = useState(emptyLogsDataset);

  useEffect(() => {
    if (isOpen) {
      if (dataset) {
        setLocal({
          id: dataset.id || `ds-${Date.now()}`,
          label: dataset.label || 'Series',
          color: dataset.color || 'blue',
          service: dataset.service || '',
          logLevel: dataset.logLevel || '',
          keyword: dataset.keyword || '',
          logql: dataset.logql || '',
          metric: dataset.metric || 'cpu'
        });
      } else {
        setLocal(emptyLogsDataset());
      }
    }
  }, [isOpen, dataset]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(local);
  };

  return (
    <div className={`modal-overlay modal-overlay--nested widget-modal-root${darkMode ? ' widget-modal--dark' : ' widget-modal--light'}`}>
      <div className="modal modal--nested">
        <div className="modal-header">
          <h2 className="modal-title">{dataset ? 'Edit data series' : 'Add data series'}</h2>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Series label (legend)</label>
            <input
              type="text"
              value={local.label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Line / bar color</label>
            <select
              value={local.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {COLOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {dataSource === 'metrics' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <select
                value={local.metric}
                onChange={(e) => handleChange('metric', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="cpu">CPU</option>
                <option value="memory">Memory</option>
                <option value="disk">Disk</option>
                <option value="network">Network</option>
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
                <select
                  value={local.service}
                  onChange={(e) => handleChange('service', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Log level</label>
                <select
                  value={local.logLevel}
                  onChange={(e) => handleChange('logLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All levels</option>
                  <option value="ERROR">Error</option>
                  <option value="WARN">Warning</option>
                  <option value="INFO">Info</option>
                  <option value="DEBUG">Debug</option>
                  <option value="TRACE">Trace</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Keyword (message contains)</label>
                <input
                  type="text"
                  value={local.keyword}
                  onChange={(e) => handleChange('keyword', e.target.value)}
                  placeholder="e.g. timeout, 500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">LogQL (optional)</label>
                <textarea
                  value={local.logql}
                  onChange={(e) => handleChange('logql', e.target.value)}
                  rows={5}
                  placeholder={'message CONTAINS "error" AND level = "ERROR"'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
              Save series
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DatasetEditorModal;
